import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit"
import {
  changePassword,
  getCCIFrameURL,
  getUser,
  login,
  resetPassword,
  sendForgotPasswordEmail,
  sendVerificationCode,
  updateUser,
  verifyCode,
} from "../api/authAPI"
import {
  LoginRequest,
  LoginResponse,
  ResetPasswordRequest,
  ResetPasswordResponse,
  smsCodeRequest,
  smsCodeResponse,
  UpdateUserData,
  UpdateUserRequest,
  UserData,
  verifyCodeRequest,
  verifyCodeResponse,
} from "../../types/userTypes"
import { RootState } from "../store"
import { hideModal, ModalTypes, showModal } from "./modalSlice"
import { updateApiHeaders } from "../api/apiConfig"
import sha1 from "sha1"

const PUBLIC_DEPLOY_URL = import.meta.env.VITE_PUBLIC_DEPLOY_URL

export type AuthState = {
  userData: UserData | null
  verificationData?: {
    verificationIdentity: string
    phoneNumber: string
  }
  rememberMe: boolean,
  usersData: LoginResponse[] | null
}

const initialState: AuthState = {
  userData: null,
  rememberMe: true,
  usersData: null,
}

export const loginEmailAsync = createAsyncThunk<LoginResponse, LoginRequest>(
  "auth/login",
  async ({ email, password }, { getState, dispatch }: any) => {
    const { rememberMe } = getState().auth
    const response: LoginResponse[] = await login(email, sha1(password))
    if (response) {
      handleLoginSuccess(response[0], rememberMe)
      dispatch(setUsersData(response))

      localStorage.setItem("USERS_UNDER_THIS_USER", JSON.stringify(response))

      return response[0]
    } else {
      throw new Error("Login failed")
    }
  },
)

export const sendVerificationCodeAsync = createAsyncThunk<
  smsCodeResponse,
  smsCodeRequest
>(
  "auth/sendVerificationCode",
  async ({ phoneNumber }, { getState, dispatch }) => {
    const response: any = await sendVerificationCode(phoneNumber)
    if (response.isSuccess) {
      const verificationData = {
        verificationIdentity: response.verificationIdentity,
        phoneNumber: phoneNumber,
      }
      dispatch(setVerificationData(verificationData))

      return response
    }
  },
)

export const verifyCodeAsync = createAsyncThunk<
  verifyCodeResponse,
  verifyCodeRequest
>(
  "auth/verifyCode",
  async ({ pin, phoneNumber }, { getState, dispatch }: any) => {
    const { verificationData, rememberMe } = getState().auth
    const response: LoginResponse[] = await verifyCode(
      pin,
      phoneNumber,
      verificationData.verificationIdentity,
    )
    if (response[0]) {
      handleLoginSuccess(response[0], rememberMe)
      dispatch(setVerificationData(null))
      dispatch(hideModal())
      
      dispatch(setUsersData(response))

      localStorage.setItem("USERS_UNDER_THIS_USER", JSON.stringify(response))

      return response[0]
    }
  },
)

export const getUserAsync = createAsyncThunk<any, void>(
  "auth/getUser",
  async () => {
    try {
      const response: UserData = await getUser()
      return response
    } catch (error) {
      console.error("There was an error!", error)
      throw new Error("There was an error!")
    }
  },
)

export const sendForgotPasswordEmailAsync = createAsyncThunk<
  string,
  { email: string }
>("auth/sendForgotPasswordEmail", async ({ email }) => {
  const response: string = await sendForgotPasswordEmail(email)
  //always show modal, email will be sent only if user is registered
  return response
})

export const resetPasswordAsync = createAsyncThunk<any, any>(
  "auth/resetPassword",
  async ({ newPassword, token }, { getState, dispatch }) => {
    const response: any = await resetPassword(sha1(newPassword), token)
    return response
  },
)

export const updateUserAsync = createAsyncThunk<any, any>(
  "auth/updateUser",
  async ({ street, city, postalCode, newPin }, { getState, dispatch }) => {
    const state = getState() as RootState
    const userData = selectUserData(state)
    if (!userData) {
      throw new Error("No user data available")
    }

    const updatedData: any = {
      accountDetails: null,
      notification: null,
      settings: null,

      personal: {
        // ...userData.personal,
        customerToken: userData.personal.customerToken,
        address: street,
        city: city,
        // postalCode: postalCode,
      },
    }
    if (newPin) {
      updatedData.drivingSettings = {
        pincode: newPin,
      }
    }else{
      updatedData.drivingSettings = null;
    }
    const response: any = await updateUser(updatedData)
    return response
  },
)
export const updateUserSettingsAsync = createAsyncThunk<any, any>(
  "auth/updateUserSettings",
  async (updateObject, { getState, dispatch }) => {
    const state = getState() as RootState
    const userData = selectUserData(state)
    if (!userData) {
      throw new Error("No user data available")
    }

    updateObject.personal = {
      customerToken: userData.personal.customerToken,
    }

    const response: any = await updateUser(updateObject)
    return response
  },
)

export const changePasswordAsync = createAsyncThunk<
  any,
  // ResetPasswordResponse,
  ResetPasswordRequest
>(
  "auth/resetPassword",
  async ({ oldPassword, newPassword }, { getState, dispatch }) => {
    try {
      const credentialId = 2345
      const response: any = await changePassword(
        sha1(oldPassword),
        sha1(newPassword),
        credentialId,
      )
      return response
    } catch (error) {
      throw new Error("There was an error!")
    }
  },
)

export const getCCIFrameURLAsync = createAsyncThunk(
  "/GetCCIFrameURL",
  async (user: any): Promise<any> => {
    const {
      firstName,
      lastName,
      email,
      address,
      city,
      userTypeId = 1,
      languageId = 2,
    } = user.personal
    const { customerToken } = user.personal
    const loginToken = localStorage.getItem("x-Token") as string

    const registrationLink = PUBLIC_DEPLOY_URL

    const redirectURL = `${registrationLink}success?x=ct%3d${customerToken}%26token%3d${loginToken}%26utId%3d${userTypeId}%26rsId%3d6%26rdnt%3d0%26langId%3d${languageId}%26email%3d${email}`
    const requestOptions = {
      RedirectURL: redirectURL,
      CustomerLastName: lastName,
      CustomerFirstName: firstName,
      Address: address,
      City: city,
      EmailAddress: email,
      Custom1: customerToken,
      Custom2: loginToken,
    }

    const response = await getCCIFrameURL(requestOptions)
    return response
  },
)

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setUserData: (state, action: PayloadAction<UserData | null>) => {
      state.userData = action.payload
    },
    setUsersData: (state, action: PayloadAction<LoginResponse[] | null>) => {
      state.usersData = action.payload
    },
    setRememberMe: (state, action: PayloadAction<boolean>) => {
      state.rememberMe = action.payload
    },
    setVerificationData: (state, action: PayloadAction<any>) => {
      state.verificationData = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getUserAsync.fulfilled, (state, action) => {
        state.userData = action.payload
      })
      .addCase(getUserAsync.rejected, (state) => { })
      .addCase(changePasswordAsync.pending, (state) => { })
      .addCase(changePasswordAsync.fulfilled, (state, action) => { })
      .addCase(changePasswordAsync.rejected, (state) => { })
  },
})

export const { setUserData, setVerificationData, setRememberMe, setUsersData } =
  authSlice.actions

export const selectUserData = (state: RootState) => state.auth.userData
export const selectRememberMe = (state: RootState) => state.auth.rememberMe
export const selectVerificationData = (state: RootState) =>
  state.auth.verificationData

export default authSlice.reducer

export const handleLoginSuccess = (data: LoginResponse, rememberMe?: boolean) => {
  const { token, customerToken } = data
  updateApiHeaders(token, customerToken)
  if (rememberMe) {
    localStorage.setItem("x-Token", token)
    localStorage.setItem("x-Customer", customerToken)
  }
}

// WIP
// export const changePinCodeAsync = createAsyncThunk<any, any>(
//   "auth/changePinCode",
//   async ({ oldPincode, newPincode }, { getState, dispatch }) => {
//     try {
//       const response: any = await resetPassword(oldPincode, newPincode)
//       return response
//     } catch (error) {
//       throw new Error("There was an error!")
//     }
//   },
// )
